import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/layout/AuthLayout';
import { Button, Input } from '../../components/ui';
import { popReturnUrl, setReturnUrl } from '../../lib/storage';
import { getUserProfile } from '../../firebase/firestore';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export default function LogIn() {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, error: authError, authLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Handle post-login redirect based on verification status
  const handlePostLoginRedirect = async (user) => {
    // Skip verifications in emulator mode
    const isEmulator = import.meta.env.VITE_USE_EMULATORS === 'true';

    // Check email verification first
    if (!isEmulator && !user.emailVerified) {
      navigate('/auth/verify-email');
      return;
    }

    // Check phone verification (need to fetch profile)
    if (!isEmulator) {
      const profile = await getUserProfile(user.uid);
      if (!profile?.phoneVerified) {
        // Store return URL so user comes back after phone verification
        const returnUrl = popReturnUrl('/');
        if (returnUrl !== '/') {
          setReturnUrl(returnUrl);
        }
        navigate('/auth/verify-phone');
        return;
      }
    }

    // All verified - go to return URL or home
    const returnUrl = popReturnUrl('/');
    navigate(returnUrl);
  };

  const onSubmit = async (data) => {
    try {
      const user = await signIn(data.email, data.password);
      await handlePostLoginRedirect(user);
    } catch (err) {
      // Error is handled by AuthContext
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const user = await signInWithGoogle();
      await handlePostLoginRedirect(user);
    } catch (err) {
      // Error is handled by AuthContext
    }
  };

  return (
    <AuthLayout
      title="Sign in to your account"
      subtitle={
        <>
          Don't have an account?{' '}
          <Link
            to="/auth/signup"
            className="font-medium text-primary hover:text-primary-hover"
          >
            Create one
          </Link>
        </>
      }
    >
      <div className="space-y-6">
        {/* Auth error */}
        {authError && (
          <div className="rounded-md bg-error-light p-4">
            <p className="text-sm text-error">{authError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Email */}
          <Input
            label="Email address"
            type="email"
            autoComplete="email"
            placeholder="john@example.com"
            error={errors.email?.message}
            dark
            {...register('email')}
          />

          {/* Password */}
          <div>
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              error={errors.password?.message}
              dark
              {...register('password')}
            />
            <div className="mt-2 text-right">
              <Link
                to="/auth/reset-password"
                className="text-sm text-primary hover:text-primary-hover"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            fullWidth
            loading={authLoading}
          >
            Sign in
          </Button>
        </form>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-midnight/10" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-cream text-midnight/50">or</span>
          </div>
        </div>

        {/* Google Sign In */}
        <Button
          type="button"
          variant="outline-dark"
          fullWidth
          onClick={handleGoogleSignIn}
          disabled={authLoading}
          iconLeft={<GoogleIcon />}
        >
          Continue with Google
        </Button>
      </div>
    </AuthLayout>
  );
}

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
